import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../shared';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

const TYPE_LABELS = {
  organized_trip: '🕋 رحلة منظمة',
  ticket: '🎫 تذكرة',
  daily_operation: '📅 عملية يومية',
  umrah_pilgrim: '👤 معتمر',
};

/**
 * Reusable Grant-Discount dialog.
 *
 * Props:
 *  - bookingType: 'organized_trip' | 'ticket' | 'daily_operation' | 'umrah_pilgrim'
 *  - bookingId: string (required)
 *  - bookingLabel?: string (display only)
 *  - receiptId?: string (link the credit note to a specific paid receipt)
 *  - onClose: () => void
 *  - onCreated?: (cr) => void
 */
export const GrantCreditNoteDialog = ({
  bookingType,
  bookingId,
  bookingLabel,
  receiptId,
  onClose,
  onCreated,
}) => {
  const { user } = useContext(AuthContext);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  // 🆕 Optional proof document (image or PDF, ≤5MB). Uploaded AFTER the credit
  // note is created (2-step pattern keeps the create endpoint JSON-based).
  const [document, setDocument] = useState(null);
  // 🆕 مصدر الخصم — افتراضياً "الخزينة" (الوصل مُحوَّل).
  const [deductionSource, setDeductionSource] = useState('cashbox');
  const [saving, setSaving] = useState(false);
  // 🆕 In-dialog "are you sure?" state — first click arms, second click submits.
  // Replaces the unreliable window.confirm() that gets blocked by Radix focus.
  const [confirmArmed, setConfirmArmed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(
          `${API}/credit-notes/_check-eligibility/${bookingType}/${bookingId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEligibility(r.data);
      } catch (e) {
        setEligibility({ eligible: false, error: e.response?.data?.detail || 'فشل التحقق من الأهلية' });
      } finally { setLoading(false); }
    };
    load();
  }, [bookingType, bookingId]);

  const isPrivileged = ['super_admin', 'general_accountant'].includes(user?.role);
  // 🆕 Non-admins can now initiate a credit-note request that lands in
  // 'pending_approval' status. The dialog adapts the wording / button
  // label so the user understands their action will be reviewed.
  const isRequester = !isPrivileged;
  const balance = eligibility?.balance;
  const maxAllowed = balance?.effective_total || 0;

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setConfirmArmed(false);
      alert('قيمة التخفيض غير صالحة — أدخل مبلغاً أكبر من صفر'); return;
    }
    if (amt > maxAllowed) {
      setConfirmArmed(false);
      alert(`القيمة تتجاوز السعر الفعّال للملف (${fmt(maxAllowed)} دج)`); return;
    }
    if (!reason.trim()) {
      setConfirmArmed(false);
      alert('سبب التخفيض إلزامي'); return;
    }
    // First click: arm the confirmation, transform button label, don't submit yet.
    if (!confirmArmed) { setConfirmArmed(true); return; }
    // Second click: submit.
    // Optional document validation — kept lightweight; backend will re-validate.
    if (document) {
      const allowed = ['image/jpeg','image/png','image/webp','image/gif','application/pdf'];
      if (!allowed.includes(document.type)) {
        setConfirmArmed(false);
        alert('نوع الملف غير مدعوم. المسموح: صورة (JPG/PNG/WebP/GIF) أو PDF'); return;
      }
      if (document.size > 5 * 1024 * 1024) {
        setConfirmArmed(false);
        alert('حجم الوثيقة يتجاوز 5MB'); return;
      }
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/credit-notes`, {
        booking_type: bookingType,
        booking_id: bookingId,
        receipt_id: receiptId || null,
        amount: amt,
        reason: reason.trim(),
        notes: notes.trim() || null,
        deduction_source: deductionSource,
      }, { headers: { Authorization: `Bearer ${token}` } });

      // 🆕 If the user attached an optional proof document, upload it now.
      // Errors here surface a soft warning but don't undo the credit-note
      // (the note itself is immutable). The user can still re-attach later
      // via the same endpoint.
      let docMsg = '';
      if (document) {
        try {
          const fd = new FormData();
          fd.append('document', document);
          await axios.post(
            `${API}/credit-notes/${r.data.id}/upload-document`,
            fd,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          docMsg = '\n📎 تم رفع وثيقة الإثبات';
        } catch (de) {
          docMsg = '\n⚠️ تم إنشاء السند لكن فشل رفع الوثيقة: '
                 + (de.response?.data?.detail || de.message || 'خطأ غير معروف');
        }
      }

      alert(`✅ تم إصدار سند التخفيض ${r.data.credit_note_number}${
        r.data.status === 'pending_approval'
          ? '\n⏳ بانتظار موافقة المدير/المحاسب — لم يُخصَم أي مبلغ بعد.'
          : ''
      }${docMsg}`);
      onCreated?.(r.data);
      onClose?.();
    } catch (e) {
      console.error('Credit note create failed:', e);
      alert(e.response?.data?.detail || e.message || 'تعذّر إنشاء السند');
      setConfirmArmed(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" dir="rtl" data-testid="grant-credit-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            💸 منح تخفيض استثنائي
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            سند نهائي وغير قابل للتعديل بعد التأكيد. الوصولات الأصلية تبقى ثابتة.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-6 text-center text-gray-500">جاري التحقق…</div>}

        {!loading && !eligibility?.eligible && (
          <div className="py-4 px-3 rounded-md bg-yellow-50 border border-yellow-300 text-sm text-yellow-900 space-y-1">
            <div className="font-semibold">⚠️ غير مؤهّل للتخفيض الاستثنائي</div>
            <div>هذه الميزة تتطلّب تسوية الملف بالكامل (الرصيد = 0).</div>
            {balance && (
              <ul className="text-xs mt-2 list-disc list-inside text-yellow-800 space-y-0.5">
                <li>السعر الإجمالي: {fmt(balance.total)} دج</li>
                <li>المدفوع: {fmt(balance.paid)} دج</li>
                <li>المتبقي: {fmt(balance.remaining)} دج</li>
              </ul>
            )}
          </div>
        )}

        {!loading && eligibility?.eligible && isRequester && (
          <div className="py-2 px-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-900">
            ℹ️ ستُسجَّل بياناتك كـ <strong>طلب معلّق</strong> وتنتظر موافقة المدير العام أو المحاسب العام.
            لا يتم خصم أي مبلغ من الخزينة أو رصيد الوكالة قبل الاعتماد.
          </div>
        )}

        {!loading && eligibility?.eligible && (
          <div className="space-y-3 py-2">
            <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm space-y-1">
              <div className="flex justify-between"><span>الملف:</span><span className="font-semibold">{bookingLabel || bookingId.slice(0,8)}</span></div>
              <div className="flex justify-between"><span>السعر الفعّال:</span><span className="font-semibold">{fmt(balance.effective_total)} دج</span></div>
              <div className="flex justify-between"><span>المدفوع:</span><span className="font-semibold text-emerald-700">{fmt(balance.paid)} دج</span></div>
              {balance.credit_balance > 0 && (
                <div className="flex justify-between"><span>رصيد دائن سابق:</span><span className="font-semibold text-amber-700">{fmt(balance.credit_balance)} دج</span></div>
              )}
            </div>
            <div>
              <Label>قيمة التخفيض (دج) <span className="text-red-600">*</span></Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="مثال: 5000"
                data-testid="cr-amount-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">الحد الأقصى المسموح: {fmt(maxAllowed)} دج</p>
            </div>
            <div>
              <Label>سبب التخفيض <span className="text-red-600">*</span></Label>
              <Textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="مثال: عميل وفي - مكافأة استثنائية"
                data-testid="cr-reason-input"
              />
            </div>

            {/* 🆕 Source of the deduction — agency balance vs central cashbox */}
            <div>
              <Label>مصدر خصم التخفيض <span className="text-red-600">*</span></Label>
              <div className="mt-1 grid grid-cols-1 gap-2">
                <label className={`cursor-pointer rounded-md border-2 p-2 text-sm flex items-start gap-2 ${deductionSource === 'cashbox' ? 'border-amber-500 bg-amber-50' : 'border-gray-300 bg-white'}`}>
                  <input
                    type="radio"
                    name="deduction_source"
                    value="cashbox"
                    checked={deductionSource === 'cashbox'}
                    onChange={() => setDeductionSource('cashbox')}
                    className="mt-1"
                    data-testid="cr-src-cashbox"
                  />
                  <div>
                    <div className="font-semibold">🏦 من الخزينة المركزية</div>
                    <div className="text-[11px] text-gray-600">الوصل **محوَّل** من الوكالة إلى الخزينة — يُسجَّل التخفيض كمصروف في المحاسبة العامة وينقص من الخزينة.</div>
                  </div>
                </label>
                <label className={`cursor-pointer rounded-md border-2 p-2 text-sm flex items-start gap-2 ${deductionSource === 'agency_balance' ? 'border-amber-500 bg-amber-50' : 'border-gray-300 bg-white'}`}>
                  <input
                    type="radio"
                    name="deduction_source"
                    value="agency_balance"
                    checked={deductionSource === 'agency_balance'}
                    onChange={() => setDeductionSource('agency_balance')}
                    className="mt-1"
                    data-testid="cr-src-agency"
                  />
                  <div>
                    <div className="font-semibold">🏢 من رصيد الوكالة</div>
                    <div className="text-[11px] text-gray-600">الوصل **غير محوَّل** بعد (لا يزال لدى الوكالة) — يُسجَّل التخفيض كمصروف للوكالة فقط، الخزينة المركزية لا تتأثر.</div>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <Label>ملاحظات (اختياري)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>

            {/* 🆕 Optional proof document */}
            <div className="p-2 border border-dashed border-amber-300 bg-amber-50/40 rounded-md">
              <Label className="text-sm font-medium text-amber-900">
                📎 وثيقة الإثبات (اختياري) <span className="text-[11px] text-gray-600">— صورة JPG/PNG/WebP أو PDF، حد أقصى 5MB</span>
              </Label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                onChange={(e) => setDocument(e.target.files?.[0] || null)}
                className="mt-2 block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-amber-600 file:text-white hover:file:bg-amber-700"
                data-testid="cr-document-input"
              />
              {document && (
                <div className="mt-2 text-xs text-emerald-700 flex items-center gap-2">
                  ✅ <span className="truncate max-w-[220px]">{document.name}</span>
                  <span className="text-gray-500">({(document.size / 1024).toFixed(0)} KB)</span>
                </div>
              )}
            </div>

            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              ⓘ هذا التخفيض سيُسجَّل كمصروف &quot;تخفيض ممنوح&quot; في المحاسبة العامة
              وينقص من رصيد الخزينة. لن يمكن تعديل أو حذف السند بعد إنشائه.
              الوصولات الأصلية تبقى ثابتة.
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          {!loading && eligibility?.eligible && (
            <Button
              onClick={submit}
              disabled={saving}
              className={confirmArmed
                ? "bg-red-600 hover:bg-red-700 animate-pulse"
                : "bg-amber-600 hover:bg-amber-700"}
              data-testid="cr-confirm-btn"
            >
              {saving
                ? '⏳ جاري الإنشاء...'
                : confirmArmed
                  ? '⚠️ اضغط للتأكيد النهائي'
                  : (isRequester ? '📤 إرسال طلب التخفيض للموافقة' : '💸 منح التخفيض')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


/**
 * Standalone management page: list of credit notes + detail dialog + PDF.
 */
const CreditNotesManagement = () => {
  const { user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('booking_type', filterType);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const r = await axios.get(
        `${API}/credit-notes${params.toString() ? `?${params}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems(r.data.items || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterType, startDate, endDate]);

  const openPdf = (cr) => {
    const token = localStorage.getItem('token');
    window.open(`${API}/credit-notes/${cr.id}/pdf?token=${encodeURIComponent(token)}`, '_blank');
  };

  const totalAmount = items.reduce((s, it) => s + (it.amount || 0), 0);

  return (
    <div className="space-y-4" dir="rtl">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-amber-800 flex items-center gap-2">
            💸 سندات التخفيض الاستثنائي
          </h2>
          <p className="text-sm text-gray-600">
            تخفيضات إدارية ممنوحة بعد التسوية الكاملة — الوصولات الأصلية تبقى ثابتة.
          </p>
        </div>
        <div className="text-sm bg-amber-50 border border-amber-200 rounded-md p-3 min-w-[200px]">
          <div className="text-gray-600">إجمالي السندات المعروضة</div>
          <div className="font-bold text-amber-800 text-xl">{fmt(totalAmount)} دج</div>
          <div className="text-xs text-gray-500">{items.length} سند</div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">🔍 فلاتر</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">نوع الملف</Label>
              <select
                className="w-full mt-1 px-3 py-2 text-sm border rounded-md bg-white"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                data-testid="cr-filter-type"
              >
                <option value="all">— الكل —</option>
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">من تاريخ</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">إلى تاريخ</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setFilterType('all'); setStartDate(''); setEndDate(''); }}
              >🧹 مسح</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">📋 القائمة</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-gray-500">جاري التحميل…</div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-gray-500">لا توجد سندات تخفيض في النطاق المحدد.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">رقم السند</TableHead>
                  <TableHead className="text-right">التاريخ</TableHead>
                  <TableHead className="text-right">نوع الملف</TableHead>
                  <TableHead className="text-right">رقم الملف</TableHead>
                  <TableHead className="text-right">المعتمر/العميل</TableHead>
                  <TableHead className="text-right">القيمة</TableHead>
                  <TableHead className="text-right">السبب</TableHead>
                  <TableHead className="text-right">منشئ السند</TableHead>
                  <TableHead className="text-right">الإجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((cr) => (
                  <TableRow key={cr.id} data-testid={`cr-row-${cr.id}`}>
                    <TableCell className="font-mono font-bold text-amber-800">{cr.credit_note_number}</TableCell>
                    <TableCell className="text-xs">{new Date(cr.created_at).toLocaleString('ar-DZ')}</TableCell>
                    <TableCell><Badge variant="outline">{TYPE_LABELS[cr.booking_type] || cr.booking_type}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{cr.booking_label || cr.booking_id?.slice(0, 8)}</TableCell>
                    <TableCell className="text-sm">{cr.pilgrim_or_client_name || '—'}</TableCell>
                    <TableCell className="font-mono font-bold text-amber-700">{fmt(cr.amount)} دج</TableCell>
                    <TableCell className="text-xs max-w-[220px] truncate" title={cr.reason}>{cr.reason}</TableCell>
                    <TableCell className="text-xs text-gray-600">{cr.created_by_name || '—'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setDetail(cr)} data-testid={`cr-detail-${cr.id}`}>👁️</Button>
                        <Button size="sm" variant="outline" onClick={() => openPdf(cr)} data-testid={`cr-pdf-${cr.id}`}>🖨️</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {detail && (
        <Dialog open onOpenChange={() => setDetail(null)}>
          <DialogContent className="max-w-lg" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-amber-800 flex items-center gap-2">
                💸 {detail.credit_note_number}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm py-2">
              {[
                ['رقم السند', detail.credit_note_number],
                ['التاريخ', new Date(detail.created_at).toLocaleString('ar-DZ')],
                ['نوع الملف', TYPE_LABELS[detail.booking_type] || detail.booking_type],
                ['رقم الملف', detail.booking_label || detail.booking_id],
                ['اسم المعتمر/العميل', detail.pilgrim_or_client_name || '—'],
                ['الوصل المرتبط', detail.receipt_id || '—'],
                ['القيمة', `${fmt(detail.amount)} ${detail.currency || 'DZD'}`],
                ['السبب', detail.reason],
                ['ملاحظات', detail.notes || '—'],
                ['منشئ السند', detail.created_by_name || '—'],
                ['الحالة', detail.status === 'approved' ? '✅ معتمد (نهائي)' : detail.status],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b py-1.5">
                  <span className="text-gray-600">{k}</span>
                  <span className="font-semibold text-right">{v}</span>
                </div>
              ))}
              <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                ⓘ السند معتمد ولا يمكن تعديله. لإجراء عكسي يرجى التواصل مع المدير.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetail(null)}>إغلاق</Button>
              <Button onClick={() => openPdf(detail)} className="bg-amber-600 hover:bg-amber-700">🖨️ طباعة PDF</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CreditNotesManagement;
